import { Component } from '@angular/core';
import { MppService } from '@app/mpp/service/mpp.service';
import {
  PopoverController,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { firstValueFrom, Observable } from 'rxjs';
import {
  IngredientUnitsCalculations,
  recipeDetailViewModel,
} from '@app/mpp/model/recipe-detail-view-model';
import { RecipeDetailService } from './services/recipe-detail.service';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { PATHS } from '@shared/constants/paths';
import {
  IngredientSearchResult,
  LastVisitedType,
  RecipeDetailTabs,
} from '@usf/ngrx-tools';
import {
  cleanServingPerRecipeValue,
  cleanServingValue,
  cleanYieldValue,
} from './helper/recipe-detail-helper';
import {
  IngredientCardClickEvent,
  IngredientDeleteParams,
  UnitsDropDownEvent,
} from '@app/mpp/model/ingredient-card-click-event';
import { ItemType, UnitOfMeasure } from '@usf/mpp-types';
import { EllipsisClickData } from '@shared/models/usf-product-card.model';
import { MppProductCardService } from '@app/mpp/shared/services/mpp-product-card/mpp-product-card.service';
import { UnitConversionHelper } from '@app/mpp/shared/helper/unit-conversion-helper';
import {
  UnitOfMeasureLabels,
  UnitType,
} from '@app/mpp/shared/enum/unit-conversion-enums';
import { PortionCalculatorHelper } from '@app/mpp/shared/helper/portion-calculator-helper';
import { IngredientCard } from '@app/mpp/model/ingredient-card-model';

@Component({
  selector: 'app-recipe-detail',
  templateUrl: './recipe-detail.page.html',
  styleUrls: ['./recipe-detail.page.scss'],
})
export class RecipeDetailPage implements ViewWillEnter, ViewWillLeave {
  viewModel$: Observable<recipeDetailViewModel>;
  imageSrc$: Observable<string>;
  breadcrumbs: Crumb[];
  tabRef = RecipeDetailTabs;
  isEditingNameDescription = false;
  isModalOpen$ = this.recipeDetailService.groupModalIsOpen$;
  isEditDetailsModalOpen$ = this.recipeDetailService.editDetailsModalIsOpen$;
  isIngredientDetailModalIsOpen$ =
    this.recipeDetailService.ingredientDetailModalIsOpen$;
  isIngredientSearchModalOpen$ =
    this.recipeDetailService.ingredientSearchModalIsOpen$;
  isProfileImageModalOpen$ = this.recipeDetailService.profileImageModalIsOpen$;
  isDeleteIngredientModalOpen$ =
    this.recipeDetailService.deleteIngredientModalIsOpen$;
  nameContainsEmojis = false;
  nameIsDuplicate = false;
  nameIsEmpty = false;
  descriptionContainsEmojis = false;
  showSubHeaderInfoIcon = false;
  searchResultsForIngredient$ =
    this.recipeDetailService.searchResultsForIngredient;
  searchResultsForProduct$ = this.recipeDetailService.typeAheadResults;
  searchTimeoutTracker$ = this.recipeDetailService.searchTimeoutTracker$;
  searchNavTimeout;
  marginTop = 1.5;
  constructor(
    public mppService: MppService,
    readonly platformService: PlatformService,
    readonly panAppState: PanAppState,
    readonly translateService: TranslateService,
    public recipeDetailService: RecipeDetailService,
    readonly popoverController: PopoverController,
    readonly mppProductCardService: MppProductCardService,
  ) {
    this.viewModel$ = this.recipeDetailService.recipeViewModel$;
  }
  ionViewWillLeave(): void {
    this.recipeDetailService.resetSplitView();
  }

  ionViewWillEnter(): void {
    this.setupBreadCrumbsAndTitle();
    this.recipeDetailService.updateShowHidePortion(false);
    this.isEditingNameDescription = false;
    this.nameContainsEmojis = false;
    this.descriptionContainsEmojis = false;
    this.marginTop = 1.5;
    this.recipeDetailService.handleGroupNameModalDismiss();
    this.recipeDetailService.dismissEditDetailsModal();
    this.recipeDetailService.dismissProfileImageModal();
    this.recipeDetailService.moveBackToSingleView();
  }

  async setupBreadCrumbsAndTitle() {
    const recipe = await firstValueFrom(this.viewModel$);
    const name =
      recipe.recipeName.substring(0, 25) +
      (recipe.recipeName.length > 25 ? '...' : '');
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.mpp',
        url: '/' + PATHS.MPP,
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.mppRecipes',
        url: '/' + PATHS.MPP + '/' + PATHS.RECIPES,
        active: false,
      },
      {
        label: name,
        url: '',
        active: true,
      },
    ];
    this.panAppState.setPageTitle(name);
    this.mppService.lastVisitedPage({
      lastVisited: LastVisitedType.RECIPE,
      id: recipe.recipeId,
    });
  }

  closePopover = async () => {
    if (this.popoverController.getTop()) {
      await this.popoverController.dismiss();
    }
  };

  goToIngredients(recipeId: string) {
    this.mppService.goToIngredients(recipeId);
  }

  shouldCloselockInfoPopover(event: any) {
    window.addEventListener('blur', this.closePopover, { once: true });
  }

  removeWindowListener = () => {
    window.removeEventListener('blur', this.closePopover);
  };

  clickShowHidePortion = (currentValue: boolean) => {
    this.recipeDetailService.updateShowHidePortion(!currentValue);
  };

  handleYieldChange(value: number, recipeID: string) {
    this.recipeDetailService.updateYield(value, recipeID);
  }

  handleYieldInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanYieldValue(event?.target.value);
    this.recipeDetailService.updateYield(cleanedValue, recipeID);
  }

  handleServingInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanServingValue(event?.target.value);
    this.recipeDetailService.updateServing(cleanedValue, recipeID);
  }

  handleServingPerRecipeInputChange(event: any, recipeID: string) {
    const cleanedValue = cleanServingPerRecipeValue(event?.target.value);
    this.recipeDetailService.updateServingPerRecipe(cleanedValue, recipeID);
  }

  handleServingChange(value: number, recipeID: string) {
    this.recipeDetailService.updateServing(value, recipeID);
  }

  handleServingPerRecipeChange(value: number, recipeID: string) {
    this.recipeDetailService.updateServingPerRecipe(value, recipeID);
  }

  handleShowServingUOM(event: Event, recipeID: string) {
    this.recipeDetailService.showServingUOMDropdown(event, recipeID);
  }

  handleShowYieldUOM(event: Event, recipeID: string) {
    this.recipeDetailService.showYieldUOMDropdown(event, recipeID);
  }

  toggleEditNameDescription() {
    this.isEditingNameDescription = !this.isEditingNameDescription;
  }

  handleShowGroup(event: Event, recipeID: string) {
    this.recipeDetailService.showGroupNameDropdown(event, recipeID);
  }

  async onNameChange(value: string) {
    this.nameContainsEmojis = /\p{Extended_Pictographic}/u.test(value);
    this.nameIsDuplicate = await this.mppService.doesMppNameExists(
      value,
      'recipe',
    );
    this.nameIsEmpty = !value || value.trim() === '';
  }

  onDescriptionChange(value: string) {
    this.descriptionContainsEmojis = /\p{Extended_Pictographic}/u.test(value);
  }

  handleRecipeNameChange(event: any, recipeID: string) {
    if (
      !this.nameContainsEmojis &&
      !this.nameIsDuplicate &&
      !this.nameIsEmpty
    ) {
      const cleanedValue = event.target.value?.trim();
      this.recipeDetailService.updateRecipeName(cleanedValue, recipeID);
    }
  }

  handleDescriptionChange(event: any, recipeID: string) {
    if (!this.descriptionContainsEmojis) {
      const cleanedValue = event.target.value?.trim();
      this.recipeDetailService.updateDescription(cleanedValue, recipeID);
    }
  }

  handleGroupNameModalDismiss() {
    this.recipeDetailService.handleGroupNameModalDismiss();
  }

  handleCustomGroupSubmit(groupName: string, recipeId: string) {
    this.recipeDetailService.handleCustomGroupSubmit(groupName, recipeId);
  }

  openEditDetailsModal(): void {
    this.recipeDetailService.openEditDetailsModal();
  }

  dismissEditDetailsModal(): void {
    this.recipeDetailService.dismissEditDetailsModal();
  }

  handleEditDetailsSubmission(
    updates: Map<string, string>,
    recipeId: string,
  ): void {
    this.recipeDetailService.handleEditDetailsSubmission(updates, recipeId);
  }

  openProfileImageModal(): void {
    this.recipeDetailService.openProfileImageModal();
  }

  dismissProfileImageModal(): void {
    this.recipeDetailService.dismissProfileImageModal();
  }

  handleProfileImageOnSave(
    updates: Map<string, string>,
    recipeId: string,
  ): void {
    this.recipeDetailService.handleProfileImageModalOnSave(updates, recipeId);
  }

  async showMobileActions(recipeId: string): Promise<void> {
    await this.recipeDetailService.showMobileActions(recipeId);
  }

  eatClick($event: Event) {
    $event.stopPropagation();
  }

  async updateMarginTopBasedOnClick(
    event: PointerEvent,
    selectedCardIndex: number,
  ) {
    const currentCardYPosition = (event as PointerEvent)?.clientY ?? 0;
    const baseMargin = 1;
    const cardHeightRem = 74 / 16;
    const maxCardOffset = 7;
    const viewModel = await firstValueFrom(this.viewModel$);
    const totalCards = viewModel.transformedIngredientData.length;
    // Calculate window boundaries
    const windowHeight = window.innerHeight;
    const topBoundary = windowHeight * 0.2;
    const bottomBoundary = windowHeight * 0.8;

    let effectiveIndex;
    if (currentCardYPosition <= topBoundary) {
      // Top 20% - stay at selected card's position unless it would push details off screen
      effectiveIndex = Math.max(
        0,
        Math.min(selectedCardIndex, totalCards - maxCardOffset),
      );
    } else if (currentCardYPosition >= bottomBoundary) {
      // Bottom 20% - show maxOffset cards above selected card
      effectiveIndex = Math.max(
        0,
        Math.min(
          totalCards - maxCardOffset,
          selectedCardIndex - (maxCardOffset - 1),
        ),
      );
    } else {
      // Middle - stay at selected card's position
      const positionRatio =
        (currentCardYPosition - topBoundary) / (bottomBoundary - topBoundary);
      const offset = Math.round(positionRatio * (maxCardOffset - 1));
      effectiveIndex = Math.max(
        0,
        Math.min(totalCards - maxCardOffset, selectedCardIndex - offset),
      );
    }

    this.marginTop = baseMargin + effectiveIndex * cardHeightRem;
  }

  async onNameClick(ingredientCard: IngredientCardClickEvent) {
    this.showSubHeaderInfoIcon =
      !!ingredientCard.ingredientCard.quantityUom &&
      ingredientCard.ingredientCard.quantity != null &&
      ingredientCard.ingredientCard.cost === 0;

    if (ingredientCard.ingredientCard.type === ItemType.USFPRODUCT) {
      if (this.platformService.platformType === PlatformEnum.desktop) {
        await this.openDesktopIngredientDetailSplitView(ingredientCard);
      } else {
        await this.openTouchIngredientDetailModal(ingredientCard);
      }
    }
  }

  openDesktopIngredientDetailSplitView = async (
    ingredientCard: IngredientCardClickEvent,
  ) => {
    await this.updateMarginTopBasedOnClick(
      ingredientCard.event as PointerEvent,
      ingredientCard.ingredientCard.index,
    );
    this.recipeDetailService.moveIntoSplitView();
    this.recipeDetailService.updateLastSelected(
      Number(ingredientCard.ingredientCard.index),
    );
    this.recipeDetailService.updateIngredientBaseCalculations(undefined);
  };

  openTouchIngredientDetailModal = async (
    ingredientCard: IngredientCardClickEvent,
  ) => {
    this.recipeDetailService.updateLastSelected(
      Number(ingredientCard.ingredientCard.index),
    );
    this.recipeDetailService.updateIngredientBaseCalculations(undefined);
    this.recipeDetailService.openIngredientDetailModal();
  };

  closeIngredientDetailsModal = () => {
    this.recipeDetailService.dismissIngredientDetailModal();
  };

  closeDetailsPane() {
    this.showSubHeaderInfoIcon = false;
    this.recipeDetailService.moveBackToSingleView();
  }

  async openIngredientSearchModal(): Promise<void> {
    this.recipeDetailService.openIngredientSearchModal();
  }

  closeIngredientSearchModal(): void {
    this.recipeDetailService.closeIngredientSearchModal();
  }

  handleProductViewAll(searchKey: string): void {
    this.closeIngredientSearchModal();
    this.searchNavTimeout = setTimeout(() => {
      this.mppService.gotToProductSearch(searchKey);
    }, 50);
  }

  handleNewIngredientSearchRequest(value: string) {
    this.recipeDetailService.handleNewIngredientSearchRequest(value);
  }

  handleNewIngredientSearchSubmission(value: IngredientSearchResult) {
    this.recipeDetailService.addIngredient(value);
    this.closeIngredientSearchModal();
  }

  handleShowIngredientUom(event: IngredientCardClickEvent, recipeID: string) {
    const ingredientId = event.ingredientCard.ingredientId;
    const domEvent = event.event;
    this.recipeDetailService.showIngredientUOMDropdown(
      domEvent,
      recipeID,
      ingredientId,
    );
  }

  handleIngredientQuantityChange(
    event: IngredientCardClickEvent,
    recipeID: string,
  ) {
    const quantityElement = event.event.target as HTMLInputElement;
    const ingredientId = event.ingredientCard.ingredientId;
    const cleanedValue = quantityElement.value?.trim();
    this.recipeDetailService.updateIngredientQuantity(
      cleanedValue,
      recipeID,
      ingredientId,
    );
  }

  handleIngredientNoteChange(
    event: IngredientCardClickEvent,
    recipeID: string,
  ) {
    const ingredientId = event.ingredientCard.ingredientId;
    const noteElement = event.event.target as HTMLInputElement;
    const cleanedValue = noteElement.value?.trim();
    this.recipeDetailService.updateIngredientNote(
      cleanedValue,
      recipeID,
      ingredientId,
    );
  }

  async handleIngredientEllipsisClick(
    $event: IngredientCardClickEvent,
    recipeId: string,
  ) {
    await this.recipeDetailService.handleIngredientEllipsisClick(
      $event,
      recipeId,
    );
  }

  openDeleteIngredientModal(deleteParams: IngredientDeleteParams): void {
    this.recipeDetailService.openDeleteIngredientModal(deleteParams);
  }

  dismissDeleteIngredientModal(): void {
    this.recipeDetailService.dismissDeleteIngredientModal();
  }

  handleDeleteIngredientSubmit(): void {
    this.recipeDetailService.handleDeleteIngredientSubmitFromState();
  }

  openProductDetails(event: number) {
    if (
      this.platformService.platformEnum.desktop !==
      this.platformService.platformType
    ) {
      this.closeIngredientDetailsModal();
      setTimeout(() => {
        this.mppService.goToProductDetails(event);
      }, 100);
    } else {
      this.mppService.goToProductDetails(event);
    }
  }

  async openProductEllipsisClick(ellipsisClickData: EllipsisClickData) {
    const buttons = await this.mppProductCardService.constructEllipsisButtons(
      ellipsisClickData,
      ellipsisClickData.isTouch
        ? () => {
            this.closeIngredientDetailsModal();
          }
        : undefined,
    );
    this.mppProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  handleClickBaseUnit(
    event: UnitsDropDownEvent,
    currentCalculations: IngredientUnitsCalculations,
  ) {
    const buttons = this.mppProductCardService.constructBaseUOMOptionsButtons(
      event.options,
      currentCalculations,
      this.platformService.platformType !== PlatformEnum.desktop,
    );
    if (this.platformService.platformType === PlatformEnum.desktop) {
      this.recipeDetailService.openSelectionsDesktop(event.event, buttons);
    } else {
      this.recipeDetailService.openSelectionsTouch(buttons);
    }
  }

  handleClickConversionUnit(
    event: UnitsDropDownEvent,
    currentCalculations: IngredientUnitsCalculations,
  ) {
    const buttons =
      this.mppProductCardService.constructConversiondUOMOptionsButtons(
        event.options,
        currentCalculations,
        this.platformService.platformType !== PlatformEnum.desktop,
      );
    if (this.platformService.platformType === PlatformEnum.desktop) {
      this.recipeDetailService.openSelectionsDesktop(event.event, buttons);
    } else {
      this.recipeDetailService.openSelectionsTouch(buttons);
    }
  }

  handleClickResultsUnit(
    event: UnitsDropDownEvent,
    currentCalculations: IngredientUnitsCalculations,
  ) {
    const buttons = this.mppProductCardService.constructFinalUOMOptionsButtons(
      event.options,
      currentCalculations,
      this.platformService.platformType !== PlatformEnum.desktop,
    );
    if (this.platformService.platformType === PlatformEnum.desktop) {
      this.recipeDetailService.openSelectionsDesktop(event.event, buttons);
    } else {
      this.recipeDetailService.openSelectionsTouch(buttons);
    }
  }

  updateBaseQunatity(
    value: number,
    currentCalculations: IngredientUnitsCalculations,
  ) {
    this.recipeDetailService.updateIngredientBaseCalculations({
      ...currentCalculations,
      baseQuantity: value,
    });
  }

  updateConversionQuantity(
    value: number,
    currentCalculations: IngredientUnitsCalculations,
  ) {
    this.recipeDetailService.updateIngredientBaseCalculations({
      ...currentCalculations,
      conversionQunatity: value,
      testQuantity: value,
    });
  }

  saveUnitsOfMeasureCalculations = async (
    netWeight: number,
    calculations: IngredientUnitsCalculations,
    measurements: UnitOfMeasure,
    productNum: number,
  ) => {
    const baseUnitType = UnitConversionHelper.getUnitType(
      calculations.baseUnit as UnitOfMeasureLabels,
    );
    const conversionUnitType = UnitConversionHelper.getUnitType(
      calculations.conversionUnit as UnitOfMeasureLabels,
    );
    let calculatedMeasurements;
    if (conversionUnitType === UnitType.LIQUID) {
      if (baseUnitType === UnitType.SOLID)
        calculatedMeasurements =
          PortionCalculatorHelper.calculateLiquidPortionsByWeight(
            netWeight,
            calculations.baseQuantity,
            calculations.conversionQunatity,
            calculations.baseUnit as UnitOfMeasureLabels,
            calculations.conversionUnit as UnitOfMeasureLabels,
            measurements,
          );

      if (baseUnitType === UnitType.PIECE)
        calculatedMeasurements =
          PortionCalculatorHelper.calculateLiquidPortionsByPieces(
            calculations.baseQuantity,
            calculations.totalPieces,
            calculations.conversionQunatity,
            calculations.conversionUnit as UnitOfMeasureLabels,
            measurements,
          );
    } else if (conversionUnitType === UnitType.PIECE) {
      if (baseUnitType === UnitType.SOLID)
        calculatedMeasurements = PortionCalculatorHelper.calculatePiecesBySolid(
          netWeight,
          calculations.baseQuantity,
          calculations.conversionQunatity,
          calculations.baseUnit as UnitOfMeasureLabels,
          measurements,
        );

      if (baseUnitType === UnitType.LIQUID)
        calculatedMeasurements =
          PortionCalculatorHelper.calculatePiecesByLiquid(
            calculations.totalCups,
            calculations.baseQuantity,
            calculations.conversionQunatity,
            calculations.baseUnit as UnitOfMeasureLabels,
            measurements,
          );
    }
    this.showSubHeaderInfoIcon = false;
    await this.recipeDetailService.updateMppProductMeasurements(
      calculatedMeasurements,
      Number(productNum),
    );
  };

  clickInfoIcon = async ($event: Event) => {
    await this.recipeDetailService.showUOMInfoPopover($event);
  };

  clickCostInfoIcon = async ($event: Event) => {
    await this.recipeDetailService.showCostInfoPopover($event);
  };

  handleDismissCostInfoPopover() {
    this.recipeDetailService.dismissCostInfoIconPopover();
  }
}
